import React, {useState, useEffect} from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    TextField,
    InputAdornment,
    Divider,
    Chip,
    Button,
    Skeleton,
    CircularProgress,
    Alert,
    Tooltip
} from '@mui/material';
import {
    Home as HomeIcon,
    LocationOn as LocationIcon,
    CalendarToday as CalendarIcon,
    CreditCard as CreditCardIcon,
    Receipt as ReceiptIcon,
    Description as DocumentIcon,
    SaveAlt as SaveIcon,
    History as HistoryIcon,
    Warning as WarningIcon
} from '@mui/icons-material';
import {format, addMonths, differenceInDays, differenceInMonths, isAfter} from 'date-fns';
import {ApiDetails, KEY} from "../../../dummyData";


const LeaseTab = ({tenantId}) => {
    // State
    const [leaseData, setLeaseData] = useState(null);
    const [leaseHistory, setLeaseHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [leaseDocument, setLeaseDocument] = useState(null);

    // Fetch lease data when component mounts
    useEffect(() => {
        const fetchLeaseData = async () => {
            setLoading(true);
            setError(null);

            try {
                // Fetch active lease
                const activeLeaseResponse = await fetch(ApiDetails + "pegasus/visionary/lease/active", {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({tenant_id: tenantId})
                });

                if (!activeLeaseResponse.ok) {
                    throw new Error(`HTTP error! Status: ${activeLeaseResponse.status}`);
                }

                const activeLeaseData = await activeLeaseResponse.json();

                if (activeLeaseData.has_active_lease) {
                    setLeaseData(activeLeaseData.lease_details);
                } else {
                    setLeaseData(null);
                }


                // Fetch lease history
                const leaseHistoryResponse = await fetch(ApiDetails + "pegasus/visionary/lease/history", {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({tenant_id: tenantId})
                });

                if (!leaseHistoryResponse.ok) {
                    throw new Error(`HTTP error! Status: ${leaseHistoryResponse.status}`);
                }

                const leaseHistoryData = await leaseHistoryResponse.json();
                setLeaseHistory(leaseHistoryData.lease_history || []);

            } catch (err) {
                console.error('Error fetching lease data:', err);
                setError('Failed to load lease information. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        if (tenantId) {
            fetchLeaseData();
        }
    }, [tenantId]);

    // Helper functions
    const formatCurrency = (amount) => {
        if (!amount) return 'N/A';
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 0
        }).format(amount);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return format(new Date(dateString), 'MMM dd, yyyy');
        } catch (error) {
            return 'Invalid date';
        }
    };

    const calculateLeaseTimeRemaining = (endDateString) => {
        if (!endDateString) return 'Ongoing';

        const endDate = new Date(endDateString);
        const today = new Date();

        if (isAfter(today, endDate)) {
            return 'Expired';
        }

        const daysRemaining = differenceInDays(endDate, today);
        const monthsRemaining = differenceInMonths(endDate, today);

        if (daysRemaining <= 0) {
            return 'Expires today';
        } else if (daysRemaining === 1) {
            return '1 day remaining';
        } else if (daysRemaining < 30) {
            return `${daysRemaining} days remaining`;
        } else if (monthsRemaining === 1) {
            return '1 month remaining';
        } else {
            return `${monthsRemaining} months remaining`;
        }
    };

    const calculateNextPaymentDate = () => {
        if (!leaseData || !leaseData.lease_start_date) return 'N/A';

        const startDate = new Date(leaseData.lease_start_date);
        const today = new Date();

        // Calculate the day of the month when rent is due (from lease start date)
        const rentDueDay = startDate.getDate();

        // Create a date object for this month's rent due date
        let nextPaymentDate = new Date(today.getFullYear(), today.getMonth(), rentDueDay);

        // If today is past the rent due date, the next payment is in the next month
        if (today.getDate() > rentDueDay) {
            nextPaymentDate = addMonths(nextPaymentDate, 1);
        }

        return format(nextPaymentDate, 'MMM dd, yyyy');
    };

    const generateUpcomingPayments = (count) => {
        if (!leaseData || !leaseData.lease_start_date || !leaseData.monthly_rent) {
            return [];
        }

        const startDate = new Date(leaseData.lease_start_date);
        const today = new Date();
        const rentDueDay = startDate.getDate();

        let nextPaymentDate = new Date(today.getFullYear(), today.getMonth(), rentDueDay);
        if (today.getDate() > rentDueDay) {
            nextPaymentDate = addMonths(nextPaymentDate, 1);
        }

        const payments = [];
        for (let i = 0; i < count; i++) {
            const paymentDate = addMonths(nextPaymentDate, i);
            const daysUntilPayment = differenceInDays(paymentDate, today);

            let status = 'Upcoming';
            if (daysUntilPayment <= 0) {
                status = 'Due Today';
            } else if (daysUntilPayment <= 7) {
                status = 'Due Soon';
            }

            payments.push({
                date: format(paymentDate, 'MMM dd, yyyy'),
                amount: leaseData.monthly_rent,
                status
            });
        }

        return payments;
    };

    // Handle document download
    const handleDownloadLeaseDocument = async () => {
        try {
            if (!leaseData || !leaseData.lease_id) {
                alert('No valid lease document found.');
                return;
            }

            // Actual implementation would be:
            // const response = await fetch(ApiDetails + `lease/document/${leaseData.lease_id}`, {
            //     method: 'GET',
            //     headers: {
            //     }
            // });
            //
            // if (!response.ok) {
            //     throw new Error(`HTTP error! Status: ${response.status}`);
            // }
            //
            // const blob = await response.blob();
            // const url = window.URL.createObjectURL(blob);
            // const link = document.createElement('a');
            // link.href = url;
            // link.download = `Lease_Agreement_${leaseData.lease_id}.pdf`;
            // document.body.appendChild(link);
            // link.click();
            // window.URL.revokeObjectURL(url);
            // document.body.removeChild(link);

            alert('Document download feature coming soon!');
        } catch (error) {
            console.error('Error downloading lease document:', error);
            alert('Failed to download lease document. Please try again later.');
        }
    };

    // Handle view lease document
    const handleViewLeaseDocument = async () => {
        try {
            if (!leaseData || !leaseData.lease_id) {
                alert('No valid lease document found.');
                return;
            }

            // Implementation for viewing the document would go here
            alert('View document feature coming soon!');
        } catch (error) {
            console.error('Error viewing lease document:', error);
            alert('Failed to view lease document. Please try again later.');
        }
    };

    // View full lease history
    const handleViewLeaseHistory = () => {
        // This would navigate to a full lease history page or open a modal
        alert('View Full Lease History feature coming soon!');
    };

    // Display a message if no active lease is found
    if (!loading && !leaseData && !error) {
        return (
            <Paper
                elevation={0}
                sx={{
                    p: 4,
                    borderRadius: '12px',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    textAlign: 'center'
                }}
            >
                <Box
                    sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        backgroundColor: '#FEF3C7',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 16px'
                    }}
                >
                    <WarningIcon sx={{fontSize: 32, color: '#D97706'}}/>
                </Box>

                <Typography variant="h6" sx={{fontWeight: 600, mb: 2}}>
                    No Active Lease Found
                </Typography>

                <Typography variant="body1" sx={{color: '#6B7280', mb: 3, maxWidth: '500px', mx: 'auto'}}>
                    You don't have an active lease agreement in our system. If you believe this is an error,
                    please contact property management.
                </Typography>

                {leaseHistory.length > 0 && (
                    <Box sx={{mt: 3}}>
                        <Typography variant="subtitle1" sx={{fontWeight: 600, mb: 2}}>
                            Previous Lease History
                        </Typography>

                        <Box sx={{maxWidth: '600px', mx: 'auto'}}>
                            {leaseHistory.map((lease, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        p: 2,
                                        border: '1px solid #E5E7EB',
                                        borderRadius: '8px',
                                        mb: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between'
                                    }}
                                >
                                    <Box>
                                        <Typography variant="body2" sx={{fontWeight: 600}}>
                                            {lease.property_name} - Unit {lease.house_number}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {formatDate(lease.lease_start_date)} - {formatDate(lease.lease_end_date)}
                                        </Typography>
                                    </Box>
                                    <Chip
                                        label={lease.lease_status}
                                        size="small"
                                        color={lease.lease_status === 'ENDED' ? 'default' : 'primary'}
                                        sx={{fontWeight: 500}}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}

                <Button
                    variant="outlined"
                    startIcon={<HistoryIcon/>}
                    sx={{
                        mt: 3,
                        borderRadius: '8px',
                        textTransform: 'none',
                        py: 1,
                        px: 3
                    }}
                    onClick={handleViewLeaseHistory}
                >
                    View Full Lease History
                </Button>
            </Paper>
        );
    }

    // Display error message if there's an error
    if (error) {
        return (
            <Alert severity="error" sx={{borderRadius: '12px', mb: 3}}>
                {error}
            </Alert>
        );
    }

    // Display loading state
    if (loading) {
        return (
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    borderRadius: '12px',
                    border: '1px solid rgba(0, 0, 0, 0.08)'
                }}
            >
                <Box sx={{p: 4, textAlign: 'center'}}>
                    <CircularProgress size={40} sx={{color: '#10B981', mb: 2}}/>
                    <Typography>Loading lease information...</Typography>
                </Box>
            </Paper>
        );
    }

    // Helper function to get duration_days from lease history
    const getActiveLeaseDuration = (leaseHistory) => {
        if (!leaseHistory || !leaseHistory.length) {
            return null;
        }

        // Find the active lease in the history
        const activeLease = leaseHistory.find(lease =>
            lease.lease_status === 'ACTIVE' || lease.lease_status === 'active'
        );

        return activeLease ? activeLease.duration_days : null;
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: 3,
                borderRadius: '12px',
                border: '1px solid rgba(0, 0, 0, 0.08)'
            }}
            className="lease-details-card"
        >
            {/* Lease Details Header */}
            <Box className="lease-header" sx={{display: 'flex', alignItems: 'center', mb: 3}}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 40,
                        height: 40,
                        borderRadius: '8px',
                        backgroundColor: '#D1FAE5',
                        mr: 2
                    }}
                    className="lease-header-icon"
                >
                    <HomeIcon sx={{color: '#10B981'}}/>
                </Box>
                <Box>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 600,
                            color: '#10B981',
                            mb: 0.5
                        }}
                        className="lease-title"
                    >
                        Lease Information
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{color: '#6B7280'}}
                        className="lease-subtitle"
                    >
                        Your current lease details and property information
                    </Typography>
                </Box>
            </Box>

            {/* Lease Status Summary */}
            <Box
                sx={{
                    p: 2,
                    mb: 4,
                    borderRadius: '8px',
                    backgroundColor: '#F0FDF4',
                    border: '1px solid #D1FAE5'
                }}
                className="lease-status-summary"
            >
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1}}>
                    <Typography variant="subtitle1" sx={{fontWeight: 600, color: '#065F46'}}>
                        Lease Status
                    </Typography>
                    <Chip
                        label={leaseData.lease_status}
                        size="small"
                        sx={{
                            backgroundColor: '#D1FAE5',
                            color: '#065F46',
                            fontWeight: 600,
                            px: 1
                        }}
                        className="lease-status-chip"
                    />
                </Box>

                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 2}}>
                    <Box>
                        <Typography variant="body2" sx={{color: '#6B7280'}}>
                            Lease Duration
                        </Typography>
                        <Typography variant="body1" sx={{fontWeight: 500, color: '#065F46'}}>
                            {(() => {
                                const durationDays = getActiveLeaseDuration(leaseHistory);
                                if (durationDays) {
                                    return `${Math.round(durationDays / 30)} months (${durationDays} days)`;
                                } else {
                                    return 'Ongoing';
                                }
                            })()}
                        </Typography>
                    </Box>

                   {/* <Box>
                        <Typography variant="body2" sx={{color: '#6B7280'}}>
                            Next Payment
                        </Typography>
                        <Typography variant="body1" sx={{fontWeight: 500, color: '#065F46'}}>
                            {calculateNextPaymentDate()}
                        </Typography>
                    </Box>*/}

                    <Divider orientation="vertical" flexItem sx={{backgroundColor: '#D1FAE5'}}/>

                    <Box>
                        <Typography variant="body2" sx={{color: '#6B7280'}}>
                            Monthly Rent
                        </Typography>
                        <Typography variant="body1" sx={{fontWeight: 500, color: '#065F46'}}>
                            {formatCurrency(leaseData.monthly_rent)}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Property Information Section */}
            <Box className="property-info-section" sx={{mb: 4}}>
                <Typography
                    variant="subtitle1"
                    sx={{
                        fontWeight: 600,
                        color: '#1F2937',
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                    className="section-title"
                >
                    <LocationIcon fontSize="small" sx={{color: '#10B981'}}/>
                    Property Information
                </Typography>

                <Grid container spacing={3}>
                    {/* Property Name */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Property"
                            name="property"
                            value={leaseData.property_name || 'N/A'}
                            disabled={true}
                            variant="outlined"
                            className="property-field"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <HomeIcon sx={{color: '#9CA3AF'}}/>
                                    </InputAdornment>
                                ),
                                readOnly: true,
                                sx: {
                                    borderRadius: '8px',
                                    '& .MuiInputBase-input': {
                                        color: '#1F2937'
                                    }
                                }
                            }}
                        />
                    </Grid>

                    {/* Unit Number */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Unit Number"
                            name="unit"
                            value={leaseData.house_number || 'N/A'}
                            disabled={true}
                            variant="outlined"
                            className="unit-field"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LocationIcon sx={{color: '#9CA3AF'}}/>
                                    </InputAdornment>
                                ),
                                readOnly: true,
                                sx: {
                                    borderRadius: '8px',
                                    '& .MuiInputBase-input': {
                                        color: '#1F2937'
                                    }
                                }
                            }}
                        />
                    </Grid>

                    {/* Location */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Location"
                            name="location"
                            value={leaseData.location || 'N/A'}
                            disabled={true}
                            variant="outlined"
                            className="location-field"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LocationIcon sx={{color: '#9CA3AF'}}/>
                                    </InputAdornment>
                                ),
                                readOnly: true,
                                sx: {
                                    borderRadius: '8px',
                                    '& .MuiInputBase-input': {
                                        color: '#1F2937'
                                    }
                                }
                            }}
                        />
                    </Grid>
                </Grid>
            </Box>

            {/* Lease Dates Section */}
            <Box className="lease-dates-section" sx={{mb: 4}}>
                <Typography
                    variant="subtitle1"
                    sx={{
                        fontWeight: 600,
                        color: '#1F2937',
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                    className="section-title"
                >
                    <CalendarIcon fontSize="small" sx={{color: '#10B981'}}/>
                    Lease Start Date
                </Typography>

                <Grid container spacing={3}>
                    {/* Lease Start */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Lease Start Date"
                            name="leaseStart"
                            value={formatDate(leaseData.lease_start_date)}
                            disabled={true}
                            variant="outlined"
                            className="lease-start-field"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <CalendarIcon sx={{color: '#9CA3AF'}}/>
                                    </InputAdornment>
                                ),
                                readOnly: true,
                                sx: {
                                    borderRadius: '8px',
                                    '& .MuiInputBase-input': {
                                        color: '#1F2937'
                                    }
                                }
                            }}
                        />
                    </Grid>

                    {/* Lease End */}
{/*
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Lease End Date"
                            name="leaseEnd"
                            value={formatDate(leaseData.lease_end_date)}
                            disabled={true}
                            variant="outlined"
                            className="lease-end-field"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <CalendarIcon sx={{color: '#9CA3AF'}}/>
                                    </InputAdornment>
                                ),
                                readOnly: true,
                                sx: {
                                    borderRadius: '8px',
                                    '& .MuiInputBase-input': {
                                        color: '#1F2937'
                                    }
                                }
                            }}
                        />
                    </Grid>
*/}

                    {/* Lease Duration */}
                   {/* <Grid item xs={12}>
                        <Box
                            sx={{
                                p: 2,
                                borderRadius: '8px',
                                backgroundColor: '#F9FAFB',
                                border: '1px solid #E5E7EB',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                Lease Duration: {' '}
                                <Typography component="span" sx={{ fontWeight: 600, color: '#10B981' }}>
                                    {(() => {
                                        const durationDays = getActiveLeaseDuration(leaseHistory);
                                        if (durationDays) {
                                            return `${Math.round(durationDays / 30)} months (${durationDays} days)`;
                                        } else {
                                            return 'Ongoing';
                                        }
                                    })()}
                                </Typography>
                            </Typography>
                        </Box>
                    </Grid>*/}
                </Grid>
            </Box>

            {/* Payment Information Section */}
            {/*<Box className="payment-info-section" sx={{mb: 4}}>
                <Typography
                    variant="subtitle1"
                    sx={{
                        fontWeight: 600,
                        color: '#1F2937',
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                    className="section-title"
                >
                    <CreditCardIcon fontSize="small" sx={{color: '#10B981'}}/>
                    Payment Information
                </Typography>

                <Grid container spacing={3}>
                     Monthly Rent
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Monthly Rent"
                            name="rentAmount"
                            value={formatCurrency(leaseData.monthly_rent)}
                            disabled={true}
                            variant="outlined"
                            className="rent-amount-field"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <CreditCardIcon sx={{color: '#9CA3AF'}}/>
                                    </InputAdornment>
                                ),
                                readOnly: true,
                                sx: {
                                    borderRadius: '8px',
                                    '& .MuiInputBase-input': {
                                        color: '#1F2937'
                                    }
                                }
                            }}
                        />
                    </Grid>

                     Security Deposit
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Security Deposit"
                            name="securityDeposit"
                            value={formatCurrency(leaseData.deposit_amount)}
                            disabled={true}
                            variant="outlined"
                            className="security-deposit-field"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <CreditCardIcon sx={{color: '#9CA3AF'}}/>
                                    </InputAdornment>
                                ),
                                readOnly: true,
                                sx: {
                                    borderRadius: '8px',
                                    '& .MuiInputBase-input': {
                                        color: '#1F2937'
                                    }
                                }
                            }}
                        />
                    </Grid>
                </Grid>
            </Box>*/}

            {/* Lease History Section */}
            {leaseHistory.length > 1 && (
                <Box className="lease-history-section" sx={{mb: 4}}>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: 600,
                            color: '#1F2937',
                            mb: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}
                        className="section-title"
                    >
                        <HistoryIcon fontSize="small" sx={{color: '#10B981'}}/>
                        Lease History
                    </Typography>

                    <Box
                        sx={{
                            border: '1px solid #E5E7EB',
                            borderRadius: '8px',
                            overflow: 'hidden'
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                backgroundColor: '#F9FAFB',
                                p: 2,
                                borderBottom: '1px solid #E5E7EB'
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    color: '#6B7280',
                                    flex: 2
                                }}
                            >
                                Property
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    color: '#6B7280',
                                    flex: 2
                                }}
                            >
                                Dates
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    color: '#6B7280',
                                    flex: 1,
                                    textAlign: 'right'
                                }}
                            >
                                Status
                            </Typography>
                        </Box>

                        {leaseHistory
                            .filter(lease => lease.lease_id !== leaseData.lease_id)
                            .slice(0, 3)
                            .map((lease, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        p: 2,
                                        borderBottom: index < 2 ? '1px solid #E5E7EB' : 'none',
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: '#4B5563',
                                            flex: 2
                                        }}
                                    >
                                        {lease.property_name} - Unit {lease.house_number}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: '#4B5563',
                                            flex: 2
                                        }}
                                    >
                                        {formatDate(lease.lease_start_date)} - {formatDate(lease.lease_end_date)}
                                    </Typography>
                                    <Box
                                        sx={{
                                            flex: 1,
                                            textAlign: 'right'
                                        }}
                                    >
                                        <Chip
                                            label={lease.lease_status}
                                            size="small"
                                            sx={{
                                                backgroundColor: lease.lease_status === 'ENDED' ? '#F3F4F6' :
                                                    lease.lease_status === 'TERMINATED' ? '#FEE2E2' : '#E0E7FF',
                                                color: lease.lease_status === 'ENDED' ? '#374151' :
                                                    lease.lease_status === 'TERMINATED' ? '#B91C1C' : '#3730A3',
                                                fontWeight: 500,
                                                fontSize: '0.7rem'
                                            }}
                                        />
                                    </Box>
                                </Box>
                            ))}
                    </Box>

                    <Box sx={{textAlign: 'center', mt: 2}}>
                        <Button
                            variant="text"
                            startIcon={<HistoryIcon/>}
                            sx={{
                                borderRadius: '8px',
                                textTransform: 'none',
                                py: 1,
                                px: 3,
                                color: '#3B82F6'
                            }}
                            onClick={handleViewLeaseHistory}
                        >
                            View Full Lease History
                        </Button>
                    </Box>
                </Box>
            )}

            {/* Lease Document Section */}
            <Box
                sx={{
                    mt: 5,
                    p: 3,
                    textAlign: 'center',
                    backgroundColor: '#F9FAFB',
                    borderRadius: '12px',
                    border: '1px solid #E5E7EB'
                }}
                className="lease-document-section"
            >
                <Box
                    sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        backgroundColor: '#EBF5FF',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 16px'
                    }}
                    className="document-icon-container"
                >
                    <DocumentIcon sx={{fontSize: 32, color: '#3B82F6'}}/>
                </Box>

                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 600,
                        color: '#1F2937',
                        mb: 1
                    }}
                >
                    Lease Agreement
                </Typography>

                <Typography
                    variant="body1"
                    sx={{
                        color: '#6B7280',
                        mb: 3,
                        maxWidth: '500px',
                        mx: 'auto'
                    }}
                >
                    Need to view or download your full lease agreement? Access it below.
                </Typography>

                <Box sx={{display: 'flex', gap: 2, justifyContent: 'center'}}>
                    <Button
                        variant="outlined"
                        startIcon={<DocumentIcon/>}
                        sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            py: 1,
                            px: 3,
                            borderColor: '#3B82F6',
                            color: '#3B82F6',
                            '&:hover': {
                                borderColor: '#2563EB',
                                backgroundColor: 'rgba(59, 130, 246, 0.04)'
                            }
                        }}
                        className="view-document-button"
                        onClick={handleViewLeaseDocument}
                    >
                        View Document
                    </Button>

                    <Button
                        variant="contained"
                        startIcon={<SaveIcon/>}
                        sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            py: 1,
                            px: 3,
                            backgroundColor: '#3B82F6',
                            '&:hover': {
                                backgroundColor: '#2563EB'
                            },
                            boxShadow: 'none'
                        }}
                        className="download-document-button"
                        onClick={handleDownloadLeaseDocument}
                    >
                        Download PDF
                    </Button>
                </Box>
            </Box>
        </Paper>
    );
};

export default LeaseTab;